import { CameraIntegration, contactSupportMessage, formatTitle } from './base';
import axios from 'axios';
import dayjs from 'dayjs';
import { post } from '../../helpers/securityService';

export class Itech extends CameraIntegration {
  constructor(...args) {
    super(...args);
    this.cameraProviderName = 'itech';

    this.cameraList = null;
    this.fetchingCameraList = null; // null | Promise
  }

  async submitCredentials(username, password) {
    const {
      config: { vigilconnectalias: dvrSerial },
      location,
    } = this.camera;

    const loginCredentials = {
      un: username,
      pw: password,
    };

    const response = await post(
      `security/itech_submit_creds/${location}/${dvrSerial}`,
      loginCredentials,
      { orgId: this.orgId },
    ).catch((err) => {
      console.error(err);
      throw Error(
        'We were unable to authenticate your 3xLogic account. Please try again later.',
      );
    });

    this.camera.accessToken = response.accessToken;
    this.isAuthenticated = true;
  }

  async getCameras() {
    if (!this.cameraList) {
      if (!this.fetchingCameraList) {
        this.fetchingCameraList = this.fetchCameraList();
      }
      await this.fetchingCameraList;
    }
  }

  async fetchCameraList() {
    const errMessage =
      'We were unable to connect to 3xLogic in order to view the list of cameras associated with this event.';
    const { accessToken, apiHost } = this.camera;

    const token = btoa(`token:${accessToken}`);
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    const { data } = await axios
      .get(`${apiHost}/api/cameras`, { headers })
      .catch((err) => {
        console.error(errMessage, { err });
        throw Error(`${errMessage} ${contactSupportMessage}`);
      });

    if (!data?.data?.length) {
      console.error('No cameras found in the camera list', { data });
      throw Error(
        'We were unable to find any cameras associated with your 3xLogic account. Please contact 3xLogic support.',
      );
    }

    if (data.statuscode !== 200) {
      console.error(errMessage, data.errormessage);
      throw Error(`${errMessage} ${contactSupportMessage}`);
    }

    this.cameraList = data.data;
  }

  async fetchThumbnails() {
    const { apiHost, accessToken } = this.camera;
    await this.getCameras();

    let { time } = this.event;
    // When coming from alerts time is in YYYYMMDDHHMMSS format so we need to convert it
    // otherwise it it already in YYYY-MM-DD HH:MM:SS format
    if (time && time.length === 14 && /^\d{14}$/.test(time)) {
      time = dayjs(time, 'YYYYMMDDHHmmss').format('YYYY-MM-DD HH:mm:ss');
    }
    // Store the formatted time as an instance variable for use in other methods
    this.formattedTime = time;

    return this.cameraList.map((camera) => ({
      src: `${apiHost}/api/cameras/${
        camera.index
      }/image/${time}?isUtcTime=0&accessToken=${encodeURIComponent(
        accessToken,
      )}`,
      title: formatTitle(camera.name),
      id: camera.index,
    }));
  }

  async fetchVideo(cameraId, seekTime) {
    const nowPlaying = {
      offsetSeconds: 0,
      seekButtons: true,
      displayTime: this.formattedTime,
    };

    const { videoHost, accessToken, commonSettings } = this.camera;

    // If video seek button pressed
    if (seekTime) {
      nowPlaying.offsetSeconds += seekTime;
      nowPlaying.displayTime = dayjs(
        nowPlaying.displayTime,
        'YYYY-MM-DD HH:mm:ss',
      )
        .add(seekTime, 'second')
        .format('YYYY-MM-DD HH:mm:ss');
      this.event.time = nowPlaying.displayTime;
    }

    await this.getCameras();

    if (cameraId)
      nowPlaying.camera = this.cameraList.find(
        (camera) => camera.index === cameraId,
      );
    else [nowPlaying.camera] = this.cameraList;

    if (Object.keys(nowPlaying.camera).length === 0) {
      console.error(
        'Could not find the camera associated with the provided camera id in the camera list',
        { cameraId, cameraList: this.cameraList },
      );
      throw Error(
        `We were unable to find the video for this camera because this camera was not found to be associated with your 3xLogic account. ${contactSupportMessage}`,
      );
    }

    const videoDuration = commonSettings?.videoDurationSeconds || 30;
    const videoEndTime = dayjs(nowPlaying.displayTime, 'YYYY-MM-DD HH:mm:ss')
      .add(videoDuration, 'seconds')
      .format('YYYY-MM-DD HH:mm:ss');

    nowPlaying.event = this.event;
    nowPlaying.description = `${nowPlaying.camera.name} from ${nowPlaying.displayTime} to ${videoEndTime}`;
    nowPlaying.cameraId = nowPlaying.camera.index;

    if (!nowPlaying.cameraId) {
      console.error(
        'Could not find the camera id from the selected camera needed to update the video player',
        nowPlaying.camera,
      );
      throw Error(
        `We were unable to identify the selected camera in order to display the associated video. ${contactSupportMessage}`,
      );
    }

    const params = {
      token: accessToken,
      camera: nowPlaying.cameraId,
      from: nowPlaying.displayTime,
      to: videoEndTime,
      resolution: '1024x768',
      skipframes: 1,
      speed: 5,
      quality: 70
    };
    nowPlaying.video = {
      src: `${videoHost}?${new URLSearchParams(params).toString()}`,
      type: 'iframe',
    };

    return nowPlaying;
  }
}
